import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import PostModal from '../components/PostModal'
import { useToast } from '../components/Toast'

const STATUS_STEPS = ['draft', 'pending', 'published']

function StatusTimeline({ status }) {
  const isRejected = status === 'rejected'

  const steps = [
    { key: 'draft',     label: 'Written' },
    { key: 'pending',   label: 'Under Review' },
    { key: 'published', label: 'Published' },
  ]

  const currentIdx = STATUS_STEPS.indexOf(status)

  return (
    <div style={tl.wrap}>
      {steps.map((step, i) => {
        const done = !isRejected && currentIdx > i
        const active = !isRejected && currentIdx === i
        const rejected = isRejected && i === 1 // rejection shown at "Under Review"

        return (
          <div key={step.key} style={tl.step}>
            {/* Connector line before */}
            {i > 0 && (
              <div style={{
                ...tl.line,
                background: done || (isRejected && i > 1) ? 'var(--rule)' : done ? 'var(--success)' : 'var(--rule)',
              }} />
            )}
            <div style={{
              ...tl.dot,
              background: rejected ? 'var(--accent)'
                : active ? 'var(--ink)'
                : done ? 'var(--success)'
                : 'var(--rule)',
              border: active ? '2px solid var(--ink)' : '2px solid transparent',
              transform: active ? 'scale(1.15)' : 'scale(1)',
            }}>
              {done && !isRejected && (
                <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              {rejected && i === 1 && (
                <span style={{ fontSize: '0.45rem', color: 'white', lineHeight: 1 }}>✕</span>
              )}
            </div>
            <span style={{
              ...tl.label,
              color: rejected && i === 1 ? 'var(--accent)'
                : active ? 'var(--ink)'
                : done ? 'var(--success)'
                : 'var(--muted)',
              fontWeight: active || (rejected && i === 1) ? 500 : 400,
            }}>
              {rejected && i === 1 ? 'Passed On' : step.label}
            </span>
          </div>
        )
      })}
    </div>
  )
}

const STATUS_META = {
  draft: {
    label: 'Draft',
    desc: 'Saved but not submitted. Submit when you\'re ready.',
    color: 'var(--muted)',
    icon: '◌',
  },
  pending: {
    label: 'Under Review',
    desc: 'The team is reading your piece. You\'ll hear back soon.',
    color: 'var(--warning)',
    icon: '◎',
  },
  published: {
    label: 'Published',
    desc: 'Live on The Margins. Thank you for writing.',
    color: 'var(--success)',
    icon: '◉',
  },
  rejected: {
    label: 'Passed On',
    desc: 'This one wasn\'t the right fit — but the next one might be.',
    color: 'var(--accent)',
    icon: '◈',
  },
}

export default function SubmissionsPage() {
  const { user, profile, loading: authLoading } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [selectedPostId, setSelectedPostId] = useState(null)

  useEffect(() => {
    if (authLoading) return
    if (!user) { navigate('/auth'); return }
    fetchPosts()
  }, [authLoading, user?.id])

  async function fetchPosts() {
    const { data } = await supabase
      .from('posts')
      .select('*')
      .eq('author_id', user.id)
      .order('created_at', { ascending: false })
    setPosts(data || [])
    setLoading(false)
  }

  async function deletePost(id) {
    if (!window.confirm('Delete this draft?')) return
    // Optimistic remove immediately
    const backup = posts.find(p => p.id === id)
    setPosts(prev => prev.filter(p => p.id !== id))
    const { error } = await supabase.from('posts').delete().eq('id', id)
    if (error) {
      // Revert
      if (backup) setPosts(prev => [...prev, backup].sort((a,b) => new Date(b.created_at)-new Date(a.created_at)))
      // If RLS error, show helpful message
      if (error.message.includes('policy') || error.code === '42501') {
        toast('Permission denied. Run SUPABASE_RUN_THIS.sql to fix.', 'error')
      } else {
        toast(error.message, 'error')
      }
    }
  }

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'draft', label: 'Drafts' },
    { key: 'pending', label: 'Under Review' },
    { key: 'published', label: 'Published' },
    { key: 'rejected', label: 'Passed On' },
  ]

  const filtered = filter === 'all' ? posts : posts.filter(p => p.status === filter)

  const counts = posts.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1
    return acc
  }, {})

  if (loading) return (
    <div style={{ paddingTop: '4.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.75rem', color: 'var(--muted)' }}>Loading…</p>
    </div>
  )

  return (
    <div style={styles.page}>
      <div className="guidelines-header">
        <p style={styles.eyebrow}>Your Writing</p>
        <h1 style={styles.title}>Submissions<br /><em style={{ color: 'var(--accent)' }}>& Drafts</em></h1>
      </div>

      <div style={styles.body}>

        {/* Summary stats */}
        {posts.length > 0 && (
          <div style={styles.statsRow}>
            {[
              { label: 'Total', value: posts.length },
              { label: 'Published', value: counts.published || 0, color: 'var(--success)' },
              { label: 'Under Review', value: counts.pending || 0, color: 'var(--warning)' },
              { label: 'Drafts', value: counts.draft || 0 },
            ].map(s => (
              <div key={s.label} style={styles.stat}>
                <div style={{ ...styles.statNum, color: s.color || 'var(--ink)' }}>{s.value}</div>
                <div style={styles.statLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter bar */}
        <div style={styles.filterBar}>
          {filters.map(f => (
            <button
              key={f.key}
              style={{ ...styles.filterBtn, ...(filter === f.key ? styles.filterActive : {}) }}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key !== 'all' && counts[f.key] > 0 && (
                <span style={styles.filterCount}>{counts[f.key]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Post list */}
        {filtered.length === 0 ? (
          <div className="empty-state">
            {filter === 'all'
              ? <><p>You haven't written anything yet.</p><Link to="/write" className="btn btn-accent" style={{ marginTop: '1rem', display: 'inline-flex' }}>Start Writing</Link></>
              : filter === 'pending'
              ? <p>No pieces under review yet.</p>
              : filter === 'rejected'
              ? <p>No unselected pieces yet.</p>
              : filter === 'published'
              ? <p>No published pieces yet.</p>
              : <p>No drafts yet.</p>
            }
          </div>
        ) : (
          <div style={styles.list}>
            {filtered.map(post => {
              const meta = STATUS_META[post.status] || STATUS_META.draft
              const date = new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
              const updated = new Date(post.updated_at || post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

              return (
                <div key={post.id} style={styles.card}>
                  <div style={styles.cardTop}>
                    <div style={styles.cardLeft}>
                      <div style={styles.cardMeta}>
                        <span style={styles.section}>{'the feed'}</span>
                        {post.tag && <><span style={styles.dot}>·</span><span style={styles.tag}>{post.tag}</span></>}
                        <span style={styles.dot}>·</span>
                        <span style={styles.date}>{date}</span>
                      </div>
                      <h3 style={styles.cardTitle}>{post.title}</h3>
                      {post.excerpt && (
                        <p style={styles.cardExcerpt}>{post.excerpt}</p>
                      )}
                    </div>

                    <div style={styles.cardRight}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ ...styles.statusIcon, color: meta.color }}>{meta.icon}</span>
                        <span style={{ ...styles.statusLabel, color: meta.color }}>{meta.label}</span>
                      </div>
                      <div style={styles.actions}>
                        {post.status === 'published' && (
                          <button className="btn btn-sm btn-ghost" onClick={() => setSelectedPostId(post.id)}>Read</button>
                        )}
                        {(post.status === 'draft' || post.status === 'rejected') && (
                          <button className="btn btn-sm btn-danger" onClick={() => deletePost(post.id)}>Delete</button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={styles.cardBottom}>
                    <StatusTimeline status={post.status} />

                    <p style={styles.statusDesc}>{meta.desc}</p>

                    {/* Rejection note */}
                    {post.status === 'rejected' && post.rejection_note && (
                      <div style={styles.rejectionNote}>
                        <span style={styles.rejectionLabel}>Editor's note —</span>
                        {' '}{post.rejection_note}
                      </div>
                    )}

                    <div style={styles.cardFooter}>
                      <span style={styles.updated}>Last updated {updated}</span>
                      {post.read_time && <span style={styles.readTime}>{post.read_time} min read</span>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* CTA to write */}
        {posts.length > 0 && (
          <div style={styles.cta}>
            <Link to="/write" className="btn btn-accent">+ Write Something New</Link>
          </div>
        )}
      </div>

      {selectedPostId && (
        <PostModal
          postId={selectedPostId}
          onClose={() => setSelectedPostId(null)}
          onNavigateWriter={id => { setSelectedPostId(null); navigate('/writer/' + id) }}
        />
      )}
    </div>
  )
}

const tl = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    marginBottom: '0.75rem',
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  line: {
    width: 32,
    height: 1,
    background: 'var(--rule)',
    flexShrink: 0,
    marginRight: '0.4rem',
  },
  dot: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.2s',
  },
  label: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginRight: '0.75rem',
    whiteSpace: 'nowrap',
  },
}

const styles = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.03em' },
  body: { maxWidth: 860, margin: '0 auto', padding: '3rem 2rem 6rem' },

  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 1,
    background: 'var(--rule)',
    border: '1px solid var(--rule)',
    marginBottom: '2rem',
  },
  stat: { background: 'var(--paper)', padding: '1.25rem 1.5rem' },
  statNum: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '2rem', fontWeight: 700, lineHeight: 1, marginBottom: '0.3rem' },
  statLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)' },

  filterBar: { display: 'flex', gap: 0, borderBottom: '1px solid var(--rule)', marginBottom: '2rem' },
  filterBtn: {
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    padding: '0.7rem 1.1rem', fontFamily: "'DM Mono', monospace", fontSize: '0.62rem',
    letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
    color: 'var(--muted)', transition: 'all 0.15s', marginBottom: -1,
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  filterActive: { borderBottomColor: 'var(--ink)', color: 'var(--ink)' },
  filterCount: {
    background: 'var(--cream)', border: '1px solid var(--rule)',
    fontSize: '0.55rem', padding: '0.1rem 0.4rem',
    fontFamily: "'DM Mono', monospace", color: 'var(--muted)',
  },

  list: { display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--rule)', border: '1px solid var(--rule)' },

  card: { background: 'var(--paper)', padding: '1.75rem 2rem' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1.5rem', marginBottom: '1.5rem' },
  cardLeft: { flex: 1 },
  cardRight: { flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.75rem' },

  cardMeta: { display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' },
  section: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)' },
  tag: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)' },
  dot: { color: 'var(--rule)', fontSize: '0.7rem' },
  date: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)' },

  cardTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.2rem', fontWeight: 700, lineHeight: 1.3, marginBottom: '0.4rem' },
  cardExcerpt: { fontSize: '0.85rem', lineHeight: 1.65, color: 'var(--muted)', marginTop: '0.4rem' },

  statusIcon: { fontSize: '0.85rem' },
  statusLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 500 },
  actions: { display: 'flex', gap: '0.5rem' },

  cardBottom: { borderTop: '1px solid var(--rule)', paddingTop: '1.25rem' },
  statusDesc: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', color: 'var(--muted)', letterSpacing: '0.04em', lineHeight: 1.6, marginBottom: '0.75rem' },

  rejectionNote: {
    fontSize: '0.85rem', lineHeight: 1.65,
    padding: '0.75rem 1rem',
    background: 'rgba(200,64,26,0.05)',
    border: '1px solid rgba(200,64,26,0.15)',
    color: 'var(--muted)',
    marginBottom: '0.75rem',
  },
  rejectionLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.6rem', letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'var(--accent)',
  },

  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  updated: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--rule)', letterSpacing: '0.06em' },
  readTime: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--muted)' },

  cta: { marginTop: '3rem', textAlign: 'center' },
}
